package com.flowyride.feature.planner.ui.components

import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.padding
import androidx.compose.material3.AlertDialog
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.SegmentedButton
import androidx.compose.material3.SegmentedButtonDefaults
import androidx.compose.material3.SingleChoiceSegmentedButtonRow
import androidx.compose.material3.Switch
import androidx.compose.material3.Text
import androidx.compose.material3.TextButton
import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.unit.dp
import com.flowyride.core.domain.model.Level
import com.flowyride.feature.planner.PlannerAction
import com.flowyride.feature.planner.PlannerUiState
import com.flowyride.feature.planner.resources.Res
import com.flowyride.feature.planner.resources.avoid_motorways
import com.flowyride.feature.planner.resources.cancel
import com.flowyride.feature.planner.resources.speed_camera_alerts
import com.flowyride.feature.planner.resources.speed_camera_alerts_enable
import com.flowyride.feature.planner.resources.speed_camera_alerts_title
import com.flowyride.feature.planner.resources.speed_camera_alerts_warning
import com.flowyride.feature.planner.resources.engine_label
import com.flowyride.feature.planner.resources.engine_not_configured
import com.flowyride.feature.planner.resources.level_high
import com.flowyride.feature.planner.resources.level_low
import com.flowyride.feature.planner.resources.level_normal
import com.flowyride.feature.planner.resources.preferences_hint_fixed_profile
import com.flowyride.feature.planner.resources.show_speed_cameras
import com.flowyride.feature.planner.resources.windingness_label
import org.jetbrains.compose.resources.StringResource
import org.jetbrains.compose.resources.stringResource

/** @param rideEditable false for an imported line: only the display and navigation options remain. */
@Composable
internal fun RideOptionsSection(
    state: PlannerUiState,
    onAction: (PlannerAction) -> Unit,
    modifier: Modifier = Modifier,
    rideEditable: Boolean = true,
) {
    var warnAboutAlerts by remember { mutableStateOf(false) }
    Column(modifier, verticalArrangement = Arrangement.spacedBy(8.dp)) {
        if (rideEditable) RouteOptions(state, onAction)
        Row(Modifier.fillMaxWidth(), verticalAlignment = Alignment.CenterVertically) {
            Text(stringResource(Res.string.show_speed_cameras), Modifier.weight(1f), style = MaterialTheme.typography.bodyMedium)
            Switch(
                checked = state.showSpeedCameras,
                onCheckedChange = { onAction(PlannerAction.ShowSpeedCamerasChanged(it)) },
            )
        }
        Row(Modifier.fillMaxWidth(), verticalAlignment = Alignment.CenterVertically) {
            Text(stringResource(Res.string.speed_camera_alerts), Modifier.weight(1f), style = MaterialTheme.typography.bodyMedium)
            Switch(
                checked = state.speedCameraAlerts,
                // Turning it on needs the rider to read the legal warning first.
                onCheckedChange = { enabled ->
                    if (enabled) warnAboutAlerts = true else onAction(PlannerAction.SpeedCameraAlertsChanged(false))
                },
            )
        }
    }
    if (warnAboutAlerts) {
        AlertDialog(
            onDismissRequest = { warnAboutAlerts = false },
            title = { Text(stringResource(Res.string.speed_camera_alerts_title)) },
            text = { Text(stringResource(Res.string.speed_camera_alerts_warning)) },
            confirmButton = {
                TextButton(onClick = {
                    warnAboutAlerts = false
                    onAction(PlannerAction.SpeedCameraAlertsChanged(true))
                }) { Text(stringResource(Res.string.speed_camera_alerts_enable)) }
            },
            dismissButton = { TextButton(onClick = { warnAboutAlerts = false }) { Text(stringResource(Res.string.cancel)) } },
        )
    }
}

@Composable
private fun RouteOptions(state: PlannerUiState, onAction: (PlannerAction) -> Unit) {
    Column(verticalArrangement = Arrangement.spacedBy(8.dp)) {
        if (state.engines.size > 1) {
            LabeledRow(stringResource(Res.string.engine_label)) {
                SegmentedChoice(
                    options = state.engines,
                    selected = state.selectedEngine,
                    label = { it.name },
                    enabled = { it.isAvailable },
                    onSelect = { onAction(PlannerAction.EngineSelected(it.id)) },
                )
            }
        }
        state.engines.filterNot { it.isAvailable }.forEach {
            Hint(stringResource(Res.string.engine_not_configured, it.name))
        }

        val preferencesEnabled = state.selectedEngine?.supportsPreferences == true
        LabeledRow(stringResource(Res.string.windingness_label)) {
            SegmentedChoice(
                options = Level.entries,
                selected = state.preferences.windingness,
                label = { stringResource(it.label) },
                enabled = { preferencesEnabled },
                onSelect = { onAction(PlannerAction.WindingnessChanged(it)) },
            )
        }
        Row(Modifier.fillMaxWidth(), verticalAlignment = Alignment.CenterVertically) {
            Text(stringResource(Res.string.avoid_motorways), Modifier.weight(1f), style = MaterialTheme.typography.bodyMedium)
            Switch(
                checked = state.preferences.avoidMotorways,
                onCheckedChange = { onAction(PlannerAction.AvoidMotorwaysChanged(it)) },
                enabled = preferencesEnabled,
            )
        }
        if (!preferencesEnabled) {
            Hint(stringResource(Res.string.preferences_hint_fixed_profile, state.selectedEngine?.name.orEmpty()))
        }
    }
}

@Composable
private fun LabeledRow(label: String, content: @Composable () -> Unit) {
    Row(Modifier.fillMaxWidth(), verticalAlignment = Alignment.CenterVertically) {
        Text(label, Modifier.padding(end = 12.dp), style = MaterialTheme.typography.labelLarge)
        content()
    }
}

@Composable
private fun <T> SegmentedChoice(
    options: List<T>,
    selected: T?,
    label: @Composable (T) -> String,
    onSelect: (T) -> Unit,
    enabled: (T) -> Boolean = { true },
) {
    SingleChoiceSegmentedButtonRow(Modifier.fillMaxWidth()) {
        options.forEachIndexed { index, option ->
            SegmentedButton(
                selected = option == selected,
                onClick = { onSelect(option) },
                enabled = enabled(option),
                shape = SegmentedButtonDefaults.itemShape(index, options.size),
                label = { Text(label(option)) },
            )
        }
    }
}

@Composable
private fun Hint(text: String) {
    Text(text, style = MaterialTheme.typography.bodySmall, color = MaterialTheme.colorScheme.onSurfaceVariant)
}

private val Level.label: StringResource
    get() = when (this) {
        Level.LOW -> Res.string.level_low
        Level.NORMAL -> Res.string.level_normal
        Level.HIGH -> Res.string.level_high
    }
